/**
 * `skaile library publish / register / yank` — the first-party publish flow.
 * A git-backed library you own carries an in-repo `skaile.manifest.yaml`; this
 * commits + pushes the library repo (publish), or takes the current remote HEAD
 * as-is (register), then registers + syncs it as a store source and publishes
 * the manifest's assets under the manifest's own `publisher` (no namespace
 * remap — first-party authors intend their declared namespace).
 *
 *   - `publish`  = commit (if dirty) + push + register + sync + publish.
 *   - `register` = same, but skip the commit/push (bytes already on GitHub).
 *   - `yank`     = unpublish the library's published assets (all, or one).
 *
 * The store REST surface is reached through the injectable {@link PublisherClient}
 * (default HTTP); git side-effects go through an injectable `spawn`. Both seams
 * keep the flow unit/integration-testable without a live store or a real remote.
 *
 * @docLink cli/commands/library
 */

import { spawnSync } from "node:child_process";
import { readFileSync } from "node:fs";
import { join } from "node:path";
import { decodeSkaileManifest, type SkPublishManifest } from "@skaile/workspaces/core";
import type { GitBackendConfig, Library } from "@skaile/workspaces/library";
import { logErr, logInfo, logOk, S } from "../helpers.ts";
import { openLibraryManager } from "../open-library.ts";
import { isStoreAuthenticated } from "../store-client.ts";
import { makeHttpPublisherClient, type PublisherClient } from "../store-publisher.ts";
import {
  bareName,
  formatNamespaceAvailability,
  preflightNamespace,
  publishToStore,
  refFor,
} from "./publish-core.ts";

/** Injectable spawn contract — the subset of `spawnSync` these helpers use. */
type SpawnLike = typeof spawnSync;

/** The fixed filename a library carries at its root for publication. */
const MANIFEST_FILENAME = "skaile.manifest.yaml";

/** The remote url of a git-backed library, or `undefined` for a local one. */
function remoteUrlOf(lib: Library): string | undefined {
  if (lib.backend !== "git") return undefined;
  return (lib.backendConfig as GitBackendConfig).url;
}

/** The publish branch of a git-backed library (defaults to `main`). */
function branchOf(lib: Library): string {
  if (lib.backend !== "git") return "main";
  return (lib.backendConfig as GitBackendConfig).branch ?? "main";
}

/**
 * Read + decode the in-repo `skaile.manifest.yaml`. Prints each error
 * diagnostic and exits (1) — mirroring the sidecar flow's validation gate.
 */
function loadManifest(libPath: string): SkPublishManifest {
  let text: string;
  try {
    text = readFileSync(join(libPath, MANIFEST_FILENAME), "utf-8");
  } catch {
    logErr(
      `No ${S.dim(MANIFEST_FILENAME)} in the library — author it (see \`skaile library create\`) ` +
        "and run `skaile validate` before publishing.",
    );
    process.exit(1);
  }
  const { manifest, diagnostics } = decodeSkaileManifest(text);
  const errors = diagnostics.filter((d) => d.severity === "error");
  if (errors.length > 0) {
    for (const d of errors) logErr(`${d.code}: ${d.message}`);
    logErr("The manifest has errors — fix them with `skaile validate` before publishing.");
    process.exit(1);
  }
  return manifest;
}

/** Run a git command in `cwd` via the injected spawn; returns the result. */
function runGit(spawn: SpawnLike, cwd: string, args: string[]) {
  return spawn("git", ["-C", cwd, ...args], { encoding: "utf-8" });
}

/**
 * Ensure `libPath` is a git repo whose `origin` points at `remoteUrl`, then
 * commit any changes and push to `branch`. Idempotent re-publish: an unchanged
 * tree makes `git commit` fail with "nothing to commit" — swallowed. Push (or
 * any other git) failures surface as thrown Errors carrying git's stderr.
 */
function commitAndPush(opts: {
  libPath: string;
  remoteUrl: string;
  branch: string;
  spawn: SpawnLike;
}): void {
  const { libPath, remoteUrl, branch, spawn } = opts;

  const gitDir = runGit(spawn, libPath, ["rev-parse", "--git-dir"]);
  if (gitDir.status !== 0) {
    const init = runGit(spawn, libPath, ["init"]);
    if (init.status !== 0) {
      throw new Error(
        `git init failed: ${(init.stderr ?? "").toString().trim() || `exit ${init.status}`}`,
      );
    }
  }

  // Point origin at the resolved remote (add if absent, else set-url).
  const getUrl = runGit(spawn, libPath, ["remote", "get-url", "origin"]);
  const originArgs =
    getUrl.status === 0
      ? ["remote", "set-url", "origin", remoteUrl]
      : ["remote", "add", "origin", remoteUrl];
  const setOrigin = runGit(spawn, libPath, originArgs);
  if (setOrigin.status !== 0) {
    throw new Error(
      `git remote failed: ${(setOrigin.stderr ?? "").toString().trim() || `exit ${setOrigin.status}`}`,
    );
  }

  const add = runGit(spawn, libPath, ["add", "-A"]);
  if (add.status !== 0) {
    throw new Error(
      `git add failed: ${(add.stderr ?? "").toString().trim() || `exit ${add.status}`}`,
    );
  }

  // Fixed bot identity via -c so the commit succeeds regardless of the author's
  // git config (the publish commit is a machine action).
  const commit = spawn(
    "git",
    [
      "-C",
      libPath,
      "-c",
      "user.email=publish@skaile.store",
      "-c",
      "user.name=skaile",
      "commit",
      "-m",
      "chore: publish",
    ],
    { encoding: "utf-8" },
  );
  if (commit.status !== 0) {
    const out = `${commit.stdout ?? ""}${commit.stderr ?? ""}`;
    if (!/nothing to commit/i.test(out)) {
      throw new Error(`git commit failed: ${out.trim() || `exit ${commit.status}`}`);
    }
  }

  const push = runGit(spawn, libPath, ["push", "origin", `HEAD:${branch}`]);
  if (push.status !== 0) {
    throw new Error(
      `git push failed: ${(push.stderr ?? "").toString().trim() || `exit ${push.status}`}`,
    );
  }
}

/**
 * Provision a public GitHub repo `github.com/<you>/<name>` via `gh` and return
 * its https url. Parses the url `gh` prints on success. Throws on failure.
 */
function createRepoViaGh(name: string, spawn: SpawnLike): string {
  const res = spawn("gh", ["repo", "create", name, "--public"], { encoding: "utf-8" });
  if (res.status !== 0) {
    const stderr = (res.stderr ?? "").toString().trim();
    throw new Error(`gh repo create ${name} failed: ${stderr || `exit ${res.status}`}`);
  }
  // `gh repo create` prints the repo url (e.g. `https://github.com/<owner>/<name>`);
  // scan combined output for the first github.com url so we capture whatever
  // owner gh resolved (the authed user).
  const out = `${res.stdout ?? ""}\n${res.stderr ?? ""}`;
  const match = out.match(/https:\/\/github\.com\/[^\s]+/);
  if (!match) {
    throw new Error(`gh repo create ${name} succeeded but printed no repo url:\n${out.trim()}`);
  }
  return match[0].replace(/\.git$/, "").trim();
}

/** Shared publish body for `publish` (pushes) and `register` (skips the push). */
async function publishLibrary(
  name: string,
  opts: {
    asset?: string;
    createRepo?: boolean;
    ref?: string;
    dryRun?: boolean;
    skipPush: boolean;
  },
  client?: PublisherClient,
  spawn: SpawnLike = spawnSync,
): Promise<void> {
  const injected = client !== undefined;
  const { manager, close } = await openLibraryManager();
  try {
    const lib = await manager.requireLibrary(name);
    const manifest = loadManifest(lib.path);

    // Resolve the remote: git-backed → its url; local + --create-repo → provision
    // and link; else stop with a hint.
    let remoteUrl = remoteUrlOf(lib);
    let wouldCreateRepo = false;
    if (!remoteUrl) {
      if (opts.createRepo) {
        if (opts.dryRun) {
          // A dry-run must not provision anything — defer the real
          // `gh repo create` to the live run and just show the would-be remote.
          wouldCreateRepo = true;
          remoteUrl = `https://github.com/<your-account>/${lib.name}`;
        } else {
          remoteUrl = createRepoViaGh(lib.name, spawn);
          await manager.updateLibrary(name, {
            backend: "git",
            backendConfig: { url: remoteUrl, branch: "main", authHint: "ssh" },
          });
          logOk(`Created + linked ${S.dim(remoteUrl)}.`);
        }
      } else {
        logErr(
          `Library "${name}" has no remote — run ${S.cmd(`skaile library link ${name} <url>`)} ` +
            "first, or pass `--create-repo` to provision a public repo.",
        );
        process.exit(1);
      }
    }

    // The manifest's own publisher is the namespace (first-party — no remap).
    const namespace = manifest.publisher ?? "<manifest publisher>";
    const targetName = opts.asset ? bareName(opts.asset) : undefined;
    const wouldBeRefs = (manifest.assets ?? [])
      .filter((a) => !targetName || a.name === targetName)
      .map((a) => refFor(a.kind, namespace, a.name, a.version ?? manifest.version));

    const sourceRef = opts.ref ?? branchOf(lib);

    if (opts.dryRun) {
      logInfo(`Publish plan for ${S.bold(name)}:`);
      logInfo(
        `  remote:     ${remoteUrl}${wouldCreateRepo ? "  (would be created via `gh repo create --public`)" : ""}`,
      );
      logInfo(`  publisher:  ${namespace}`);
      // Best-effort namespace-availability probe — a read-only GET, only when
      // signed in (or a client is injected). A failure keeps dry-run offline-safe.
      if (manifest.publisher && (injected || isStoreAuthenticated())) {
        try {
          const avail = await (client ?? makeHttpPublisherClient()).namespaceAvailability(
            manifest.publisher,
          );
          logInfo(`  namespace:  ${formatNamespaceAvailability(avail)}`);
        } catch {
          // Store unreachable / not signed in — skip the advisory, never fail.
        }
      }
      logInfo(`  source ref: ${sourceRef}`);
      logInfo(`  would publish ${wouldBeRefs.length} asset(s):`);
      for (const r of wouldBeRefs) logInfo(`    ${r}`);
      return;
    }

    // A real publish makes store calls, so it needs a token (injected fakes bypass).
    if (!injected && !isStoreAuthenticated()) {
      logErr("Not signed in — run `skaile store login` first.");
      process.exit(1);
    }

    const pub = client ?? makeHttpPublisherClient();

    // Preflight the namespace BEFORE the public git push, so a reserved/unclaimed
    // namespace fails fast with guidance instead of after publishing bytes.
    if (manifest.publisher) {
      const gate = await preflightNamespace(pub, manifest.publisher);
      if (!gate.ok) {
        logErr(gate.message ?? `Cannot publish under @${manifest.publisher}.`);
        process.exit(1);
      }
    }

    // publish commits + pushes the library repo; register takes the remote HEAD as-is.
    if (!opts.skipPush) {
      // `git add -A` stages every non-ignored file → a PUBLIC push. Surface it so
      // an unexpected file in the working tree is not silently published.
      logInfo(
        `Committing the library and pushing to the PUBLIC remote ${S.dim(remoteUrl)} ` +
          "(.gitignore honored; anything else in the working tree becomes public).",
      );
      commitAndPush({ libPath: lib.path, remoteUrl, branch: branchOf(lib), spawn });
      logOk(`Pushed ${S.dim(name)} to ${S.dim(remoteUrl)}.`);
    }

    const { published } = await publishToStore({
      sourceUrl: remoteUrl,
      ref: sourceRef,
      manifest,
      assetName: targetName,
      client: pub,
    });

    if (published.length > 0) {
      logInfo(`Consumers can now \`skaile add ${published[0]}\`.`);
    }
  } finally {
    close();
  }
}

/**
 * `skaile library publish <lib>` — commit + push the library repo, register +
 * sync it as a store source, and publish its manifest's assets under the
 * manifest publisher. See the module header for the full flow.
 */
export async function runLibraryPublish(
  name: string,
  opts: { asset?: string; createRepo?: boolean; ref?: string; dryRun?: boolean },
  client?: PublisherClient,
  spawn: SpawnLike = spawnSync,
): Promise<void> {
  await publishLibrary(name, { ...opts, skipPush: false }, client, spawn);
}

/**
 * `skaile library register <lib>` — same as publish but skips the commit/push
 * (the bytes are already on GitHub at the current remote HEAD). Still requires a
 * git-backed remote.
 */
export async function runLibraryRegister(
  name: string,
  opts: { asset?: string; ref?: string; dryRun?: boolean },
  client?: PublisherClient,
  spawn: SpawnLike = spawnSync,
): Promise<void> {
  await publishLibrary(name, { ...opts, skipPush: true }, client, spawn);
}

/**
 * `skaile library yank <lib>` — unpublish the library's published assets from
 * the store (all, or the single asset matching `opts.asset`'s name).
 * Auth-gated like publish.
 */
export async function runLibraryYank(
  name: string,
  opts: { asset?: string },
  client?: PublisherClient,
): Promise<void> {
  const injected = client !== undefined;
  const { manager, close } = await openLibraryManager();
  try {
    const lib = await manager.requireLibrary(name);
    const remoteUrl = remoteUrlOf(lib);
    if (!remoteUrl) {
      logErr(`Library "${name}" has no remote — nothing published to yank.`);
      process.exit(1);
    }

    if (!injected && !isStoreAuthenticated()) {
      logErr("Not signed in — run `skaile store login` first.");
      process.exit(1);
    }

    const pub = client ?? makeHttpPublisherClient();
    const rows = await pub.sourcesList();
    const source = rows.find((r) => r.url === remoteUrl);
    if (!source) {
      logErr(`Library "${name}" is not registered in the store — nothing to yank.`);
      process.exit(1);
    }

    const targetName = opts.asset ? bareName(opts.asset) : undefined;
    const candidates = (await pub.sourceCandidates(source.id)).filter(
      (c) => c.published && (!targetName || c.name === targetName),
    );
    if (candidates.length === 0) {
      logInfo("No published assets matched — nothing to yank.");
      return;
    }

    const yanked: string[] = [];
    for (const c of candidates) {
      try {
        await pub.assetUnpublish(c.id);
        yanked.push(`${c.kind}:${c.name}`);
      } catch (err) {
        logErr(
          `Failed to yank ${c.kind}:${c.name}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    }

    logOk(`Yanked ${yanked.length} asset(s):`);
    for (const r of yanked) logInfo(`  ${r}`);
  } finally {
    close();
  }
}
